import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioPagamentos } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaCreditCard, FaMoneyBillWave, FaQrcode, FaMobileAlt, FaChartPie, FaPercentage } from 'react-icons/fa';
import { MdPayment, MdOnlinePrediction, MdPointOfSale, MdTrendingUp } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import regionConfig from '../../config/regionConfig';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    AreaChart, Area
} from 'recharts';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 250px;
        }
    }
`;

const FullWidthChart = styled(ChartCard)`
    grid-column: 1 / -1;

    .chart-container {
        height: 350px;
    }
`;

const PaymentMethodsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`;

const PaymentMethodCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 24px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.color || 'rgb(49, 140, 213)'};
    }

    .header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;

        .icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            background: ${props => props.bgColor || 'rgba(49, 140, 213, 0.1)'};
            color: ${props => props.color || 'rgb(49, 140, 213)'};
        }

        .title {
            font-size: 18px;
            font-weight: 600;
            color: #333;
        }
    }

    .stats {
        display: flex;
        justify-content: space-between;
        margin-top: auto;

        .stat {
            .value {
                font-size: 20px;
                font-weight: 700;
                color: #333;
            }

            .label {
                font-size: 12px;
                color: #666;
            }
        }
    }

    .percentage-bar {
        width: 100%;
        height: 6px;
        background: #e9ecef;
        border-radius: 3px;
        margin-top: 16px;
        overflow: hidden;

        .fill {
            height: 100%;
            border-radius: 3px;
            background: ${props => props.color || 'rgb(49, 140, 213)'};
            transition: width 0.5s ease;
        }
    }

    .percentage-text {
        text-align: right;
        margin-top: 4px;
        font-size: 12px;
        font-weight: 600;
        color: ${props => props.color || 'rgb(49, 140, 213)'};
    }
`;

const OnlineOfflineComparison = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const ComparisonCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 24px;
    text-align: center;

    .icon-container {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        margin: 0 auto 16px;

        &.online {
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(76, 217, 100, 0.2));
            color: #28a745;
        }

        &.offline {
            background: linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 193, 7, 0.2));
            color: #ff9800;
        }
    }

    .title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
    }

    .description {
        font-size: 13px;
        color: #666;
        margin-bottom: 16px;
    }

    .main-value {
        font-size: 32px;
        font-weight: 700;
        color: #333;
        margin-bottom: 4px;
    }

    .sub-value {
        font-size: 14px;
        color: #666;
    }

    .percentage-bar {
        width: 100%;
        height: 8px;
        background: #e9ecef;
        border-radius: 4px;
        margin-top: 16px;
        overflow: hidden;

        .fill {
            height: 100%;
            border-radius: 4px;
            transition: width 0.5s ease;

            &.online {
                background: linear-gradient(135deg, #28a745, #4cd964);
            }

            &.offline {
                background: linear-gradient(135deg, #ff9800, #ffc107);
            }
        }
    }

    .percentage-text {
        margin-top: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #666;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const PAYMENT_CONFIG = {
    pix: { icon: FaQrcode, color: '#00b894', bgColor: 'rgba(0, 184, 148, 0.1)', label: 'PIX' },
    dinheiro: { icon: FaMoneyBillWave, color: '#28a745', bgColor: 'rgba(40, 167, 69, 0.1)', label: 'Dinheiro' },
    credito: { icon: FaCreditCard, color: '#667eea', bgColor: 'rgba(102, 126, 234, 0.1)', label: 'Crédito' },
    debito: { icon: FaCreditCard, color: '#318CD5', bgColor: 'rgba(49, 140, 213, 0.1)', label: 'Débito' },
    vale_refeicao: { icon: FaMobileAlt, color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.1)', label: 'Vale Refeição' },
    vale_alimentacao: { icon: FaMobileAlt, color: '#e17055', bgColor: 'rgba(225, 112, 85, 0.1)', label: 'Vale Alimentação' },
    online: { icon: MdOnlinePrediction, color: '#28a745', bgColor: 'rgba(40, 167, 69, 0.1)', label: 'Online' }
};

const COLORS = ['#00b894', '#28a745', '#667eea', '#318CD5', '#ff9800', '#e17055', '#6c5ce7'];

const RelatorioPagamentos = () => {
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const getPaymentLabel = (tipo) => {
        const typeKey = tipo?.toLowerCase();
        const keyMap = {
            pix: 'payments.types.pix',
            dinheiro: 'payments.types.dinheiro',
            credito: 'payments.types.credito',
            debito: 'payments.types.debito',
            vale_refeicao: 'payments.types.vale_refeicao',
            vale_alimentacao: 'payments.types.vale_alimentacao',
            online: 'payments.types.online'
        };
        return keyMap[typeKey] ? t(keyMap[typeKey]) : tipo || t('payments.types.other');
    };

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            total_pedidos: 0,
            faturamento_total: 0,
            formas_utilizadas: 0,
            ticket_medio: 0
        },
        online_vs_offline: {
            online: { pedidos: 0, faturamento: 0, percentual: 0 },
            offline: { pedidos: 0, faturamento: 0, percentual: 0 }
        },
        por_tipo: [],
        transacoes_online: {
            total: 0,
            pagas: 0,
            falhadas: 0,
            taxa_sucesso: 0,
            total_taxas: 0
        }
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioPagamentos(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-PAGAMENTOS] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Preparar dados para gráfico de pizza
    const pieData = (data.por_tipo || []).map(m => ({
        name: getPaymentLabel(m.tipo),
        value: m.faturamento || 0,
        pedidos: m.quantidade || 0
    }));

    // Preparar dados para gráfico de barras
    const barData = (data.por_tipo || []).map(m => ({
        name: getPaymentLabel(m.tipo),
        pedidos: m.quantidade || 0,
        faturamento: m.faturamento || 0
    }));

    // Evolução não disponível no backend atual
    const evolucaoData = [];

    // Calcular total para porcentagens
    const totalFaturado = data.resumo?.faturamento_total || 1;

    const formatCurrency = (value) => {
        return formatPriceWithSymbol(value || 0);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Verifica se o campo deve ser formatado como moeda
            const isCurrencyField = (name, dataKey) => {
                const currencyKeywords = ['faturamento', 'valor', 'value', 'total', 'ticket', 'preco', 'receita'];
                const lowerName = (name || '').toLowerCase();
                const lowerKey = (dataKey || '').toLowerCase();
                return currencyKeywords.some(kw => lowerName.includes(kw) || lowerKey.includes(kw));
            };

            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#318CD5', fontSize: '13px' }}>
                            {entry.name}: {isCurrencyField(entry.name, entry.dataKey)
                                ? formatCurrency(entry.value)
                                : entry.value?.toLocaleString?.(regionConfig.currency.locale) || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getPaymentIcon = (methodId) => {
        const config = PAYMENT_CONFIG[methodId?.toLowerCase()];
        if (config) {
            const Icon = config.icon;
            return <Icon />;
        }
        return <FaCreditCard />;
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdPayment /></div>
                            <h1 className="page-title">{t('payments.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue">
                                        <MdPayment />
                                    </div>
                                    <span className="label">{t('payments.totalOrders')}</span>
                                    <span className="value">{data.resumo?.total_pedidos || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <FaMoneyBillWave />
                                    </div>
                                    <span className="label">{t('payments.totalRevenue')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.faturamento_total || 0)}
                                    </span>
                                    <span className="sub">{t('common.allMethods')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple">
                                        <MdOnlinePrediction />
                                    </div>
                                    <span className="label">{t('payments.onlinePayments')}</span>
                                    <span className="value">{data.online_vs_offline?.online?.pedidos || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.online_vs_offline?.online?.percentual || 0).toFixed(1) })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange">
                                        <MdPointOfSale />
                                    </div>
                                    <span className="label">{t('payments.offlinePayments')}</span>
                                    <span className="value">{data.online_vs_offline?.offline?.pedidos || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.online_vs_offline?.offline?.percentual || 0).toFixed(1) })}</span>
                                </MetricCard>
                            </CardsGrid>

                            <OnlineOfflineComparison>
                                <ComparisonCard>
                                    <div className="icon-container online">
                                        <MdOnlinePrediction />
                                    </div>
                                    <span className="title">{t('payments.onlineTitle')}</span>
                                    <span className="description">{t('payments.onlineDesc')}</span>
                                    <div className="main-value">
                                        {formatPriceWithSymbol(data.online_vs_offline?.online?.faturamento || 0)}
                                    </div>
                                    <div className="sub-value">
                                        {data.online_vs_offline?.online?.pedidos || 0} {t('common.orders').toLowerCase()}
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill online"
                                            style={{ width: `${parseFloat(data.online_vs_offline?.online?.percentual || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('payments.ofRevenue', { value: parseFloat(data.online_vs_offline?.online?.percentual || 0).toFixed(1) })}
                                    </span>
                                </ComparisonCard>

                                <ComparisonCard>
                                    <div className="icon-container offline">
                                        <MdPointOfSale />
                                    </div>
                                    <span className="title">{t('payments.offlineTitle')}</span>
                                    <span className="description">{t('payments.offlineDesc')}</span>
                                    <div className="main-value">
                                        {formatPriceWithSymbol(data.online_vs_offline?.offline?.faturamento || 0)}
                                    </div>
                                    <div className="sub-value">
                                        {data.online_vs_offline?.offline?.pedidos || 0} {t('common.orders').toLowerCase()}
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill offline"
                                            style={{ width: `${parseFloat(data.online_vs_offline?.offline?.percentual || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('payments.ofRevenue', { value: parseFloat(data.online_vs_offline?.offline?.percentual || 0).toFixed(1) })}
                                    </span>
                                </ComparisonCard>
                            </OnlineOfflineComparison>

                            <PaymentMethodsGrid>
                                {(data.por_tipo || []).map((method, index) => {
                                    const config = PAYMENT_CONFIG[method.tipo?.toLowerCase()] || {
                                        icon: FaCreditCard,
                                        color: COLORS[index % COLORS.length],
                                        bgColor: `rgba(${index * 30}, ${100 + index * 20}, ${150 + index * 10}, 0.1)`,
                                        label: method.tipo || 'Outros'
                                    };
                                    const Icon = config.icon;
                                    const percentage = ((method.faturamento || 0) / totalFaturado * 100);

                                    return (
                                        <PaymentMethodCard
                                            key={method.tipo || index}
                                            color={config.color}
                                            bgColor={config.bgColor}
                                        >
                                            <div className="header">
                                                <div className="icon">
                                                    <Icon />
                                                </div>
                                                <span className="title">{getPaymentLabel(method.tipo)}</span>
                                            </div>
                                            <div className="stats">
                                                <div className="stat">
                                                    <div className="value">{method.quantidade || 0}</div>
                                                    <div className="label">{t('common.orders')}</div>
                                                </div>
                                                <div className="stat">
                                                    <div className="value">
                                                        {formatPriceWithSymbol(method.faturamento || 0)}
                                                    </div>
                                                    <div className="label">{t('common.revenue')}</div>
                                                </div>
                                                <div className="stat">
                                                    <div className="value">
                                                        {formatPriceWithSymbol((method.faturamento || 0) / Math.max(method.quantidade || 1, 1))}
                                                    </div>
                                                    <div className="label">{t('common.avgTicket')}</div>
                                                </div>
                                            </div>
                                            <div className="percentage-bar">
                                                <div className="fill" style={{ width: `${percentage}%` }} />
                                            </div>
                                            <span className="percentage-text">{percentage.toFixed(1)}%</span>
                                        </PaymentMethodCard>
                                    );
                                })}
                            </PaymentMethodsGrid>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <FaChartPie />
                                        {t('payments.distributionByMethod')}
                                    </h3>
                                    <div className="chart-container">
                                        {pieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={100}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                        labelLine={false}
                                                    >
                                                        {pieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <FaPercentage />
                                        {t('payments.ordersByMethod')}
                                    </h3>
                                    <div className="chart-container">
                                        {barData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={barData} layout="vertical">
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis type="number" />
                                                    <YAxis type="category" dataKey="name" width={100} />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="pedidos" fill="#318CD5" radius={[0, 8, 8, 0]}>
                                                        {barData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            {evolucaoData.length > 0 && (
                                <FullWidthChart>
                                    <h3>
                                        <MdTrendingUp />
                                        {t('payments.paymentEvolution')}
                                    </h3>
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={evolucaoData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="data" />
                                                <YAxis />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Area type="monotone" dataKey="pix" name={t('payments.types.pix')} stackId="1" stroke="#00b894" fill="#00b894" />
                                                <Area type="monotone" dataKey="dinheiro" name={t('payments.types.dinheiro')} stackId="1" stroke="#28a745" fill="#28a745" />
                                                <Area type="monotone" dataKey="credito" name={t('payments.types.credito')} stackId="1" stroke="#667eea" fill="#667eea" />
                                                <Area type="monotone" dataKey="debito" name={t('payments.types.debito')} stackId="1" stroke="#318CD5" fill="#318CD5" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </FullWidthChart>
                            )}
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioPagamentos;
